import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDown, BadgeCheck, Dumbbell, Heart, Leaf, WheatOff } from "lucide-react";

const LINES = [
  <>Better bakes</>,
  <>
    for the <em className="italic text-[#6A2C3E]">ones</em>
  </>,
  <>
    you love.
    <Heart className="inline-block w-[0.55em] h-[0.55em] ml-3 -mt-[0.15em] text-[#C86D51] fill-current" />
  </>,
];

const EASE = [0.22, 1, 0.36, 1];

function Tilt({ children }) {
  const ref = useRef(null);
  const rx = useSpring(useMotionValue(0), { stiffness: 140, damping: 16 });
  const ry = useSpring(useMotionValue(0), { stiffness: 140, damping: 16 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    ry.set(px * 14);
    rx.set(-py * 14);
  };

  return (
    <motion.div
      ref={ref}
      data-testid="hero-tilt-boxes"
      onMouseMove={onMove}
      onMouseLeave={() => {
        rx.set(0);
        ry.set(0);
      }}
      style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
      className="relative h-[420px] sm:h-[500px] lg:h-[560px] [perspective:1200px]"
    >
      {children}
    </motion.div>
  );
}

export default function Hero() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const yBoxes = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -40]);

  return (
    <section id="top" ref={sectionRef} className="relative min-h-screen flex items-center overflow-hidden pt-[112px]">
      <div className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-[#F8C8DC]/50 blur-[120px]" />
      <div className="absolute -bottom-40 -right-24 w-[520px] h-[520px] rounded-full bg-[#D1E8E2]/60 blur-[120px]" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-0 grid lg:grid-cols-12 gap-12 lg:gap-6 items-center w-full">
        <motion.div style={{ y: yText }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
            className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.3em] text-[#6E5E57] mb-6"
          >
            Wholesome ingredients. Delicious bakes.
          </motion.p>

          <h1 data-testid="hero-headline" className="font-serif font-bold tracking-tight leading-[1.04] text-[#2C221E] text-5xl sm:text-6xl lg:text-7xl">
            {LINES.map((line, i) => (
              <span key={i} className="hero-mask-line">
                <motion.span
                  className="block"
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.25 + i * 0.14, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8, ease: EASE }}
            className="mt-7 max-w-xl"
          >
            <p className="text-base sm:text-lg text-[#6E5E57] leading-relaxed">
              At Almo Bakes, we believe a delicious cake can start with better ingredients.
              Our baking mixes are thoughtfully made with organic almond flour, organic coconut sugar
              and simple, carefully chosen ingredients — so you can bake something wholesome
              without compromising on taste.
            </p>
            <p className="mt-3 text-base sm:text-lg text-[#6E5E57] leading-relaxed">
              No complicated recipes. No long ingredient lists. Just an easy way to turn a few
              simple additions into a soft, delicious homemade cake.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.95, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href="/mixes"
              data-testid="hero-cta-mixes"
              className="rounded-full bg-[#2C221E] text-[#FAF7F2] px-8 py-4 text-sm font-medium tracking-wide hover:bg-[#6A2C3E] hover:scale-[1.03] active:scale-[0.97] transition-all duration-300"
            >
              Meet the Mixes
            </a>
            <a
              href="#story"
              data-testid="hero-cta-story"
              className="rounded-full border border-[#2C221E]/25 px-8 py-4 text-sm font-medium tracking-wide text-[#2C221E] hover:bg-[#2C221E] hover:text-[#FAF7F2] transition-all duration-300"
            >
              Our Story
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.2 }}
            className="mt-12 flex flex-wrap gap-x-10 gap-y-4"
          >
            {[
              ["7", "pantry-pure ingredients"],
              ["5 min", "one-bowl prep"],
              ['8″ cake', "or 9 cupcakes"],
            ].map(([big, small]) => (
              <div key={small}>
                <p className="font-serif text-2xl sm:text-3xl font-semibold text-[#2C221E]">{big}</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6E5E57] mt-1">{small}</p>
              </div>
            ))}
          </motion.div>
        </motion.div>

        <motion.div style={{ y: yBoxes }} className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
          >
            <Tilt>
              <div className="absolute inset-x-2 top-4 bottom-4 rounded-[2rem] overflow-hidden shadow-2xl shadow-[#2C221E]/25 -rotate-2 spotlight-frame">
                <img src="/assets/story-cake.png" alt="Freshly baked organic almond flour cake topped with toasted almond flakes" className="w-full h-full object-cover" />
              </div>

              <div className="absolute left-0 top-2 animate-float-soft" style={{ animationDelay: "0.6s" }}>
                <span className="inline-flex items-center gap-2 rounded-full bg-[#FAF7F2]/90 backdrop-blur border border-[#2C221E]/10 px-4 py-2 text-xs font-medium text-[#2C221E] shadow-lg">
                  <BadgeCheck className="w-3.5 h-3.5 text-[#2A524A]" /> Certified organic
                </span>
              </div>
              <div className="absolute -left-2 sm:left-4 bottom-16 animate-float-soft">
                <span className="inline-flex items-center gap-2 rounded-full bg-[#FAF7F2]/90 backdrop-blur border border-[#2C221E]/10 px-4 py-2 text-xs font-medium text-[#2C221E] shadow-lg">
                  <Leaf className="w-3.5 h-3.5 text-[#C86D51]" /> No refined sugar
                </span>
              </div>
              <div className="absolute right-2 bottom-6 animate-float-soft" style={{ animationDelay: "1.2s" }}>
                <span className="inline-flex items-center gap-2 rounded-full bg-[#FAF7F2]/90 backdrop-blur border border-[#2C221E]/10 px-4 py-2 text-xs font-medium text-[#2C221E] shadow-lg">
                  <WheatOff className="w-3.5 h-3.5 text-[#2A524A]" /> Gluten free
                </span>
              </div>
              <div className="absolute right-4 top-4 animate-float-soft" style={{ animationDelay: "2s" }}>
                <span className="inline-flex items-center gap-2 rounded-full bg-[#FAF7F2]/90 backdrop-blur border border-[#2C221E]/10 px-4 py-2 text-xs font-medium text-[#2C221E] shadow-lg">
                  <Dumbbell className="w-3.5 h-3.5 text-[#6A2C3E]" /> High protein
                </span>
              </div>
            </Tilt>
          </motion.div>
        </motion.div>
      </div>

      <motion.a
        href="#how-to-bake"
        data-testid="hero-scroll-indicator"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2 text-[#6E5E57]"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
          <ArrowDown className="w-4 h-4" />
        </motion.span>
      </motion.a>
    </section>
  );
}
